import isArray from 'lodash/isArray';
import { applicationFormProgressKey } from '../ApplicationApplets/Online/OnlineApplicationForm/FormProgressState/ApplicationFormProgressStateManager';
import ExecutionContext from '../ExecutionContext';
import { AddingDifferentApplicationTypeAttempt } from '~/Features/ApplicationEvents/ApplicationEvents';
import { getStoredCampusApplicationChoices } from '~/Features/CampusApplication/CampusApplicationProcessUtils';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import { OnlineApplicationItemAdded, OnlineApplicationItemRemoved } from './Events/OnlineApplicationEvents';
import type { OnlineApplicationChoice } from './Model';
import { getStoredOnlineApplicationChoices, OnlineApplicationStateStorageKey } from './OnlineApplicationProcessUtils';

export interface IOnlineApplicationProcessState {
    append(items: OnlineApplicationChoice | OnlineApplicationChoice[]): boolean;

    getAll(): OnlineApplicationChoice[];

    clear(): void;

    remove(subjectCode: string): void;

    setAll(items: OnlineApplicationChoice[]): boolean;
}

export const OnlineApplicationProcessState: () => IOnlineApplicationProcessState = () => {
    const isCampusApplicationInProgress = () => getStoredCampusApplicationChoices().length > 0;

    const append = (choices: OnlineApplicationChoice | OnlineApplicationChoice[]): boolean => {
        ExecutionContext.ThrowIfServerSide();

        if (isCampusApplicationInProgress()) {
            MessageBroker.publish(new AddingDifferentApplicationTypeAttempt());

            return false;
        }

        const currentChoices = getAll();
        const choicesFromBasket = isArray(choices) ? choices : [choices];

        let choiceShouldBeAdded = true;
        for (const choiceFromBasket of choicesFromBasket) {
            let currentChoiceIndex = -1;
            for (const currentChoice of currentChoices) {
                currentChoiceIndex++;

                if (currentChoice.subject.code !== choiceFromBasket.subject.code) {
                    continue;
                }

                if (currentChoice.parentStudy.timeType !== choiceFromBasket.parentStudy.timeType) {
                    currentChoices.splice(currentChoiceIndex, 1);
                    break;
                }

                choiceShouldBeAdded = false;
                break;
            }

            if (choiceShouldBeAdded) {
                currentChoices.push(choiceFromBasket);
            }
        }

        localStorage.setItem(OnlineApplicationStateStorageKey, JSON.stringify(currentChoices));
        MessageBroker.publish(new OnlineApplicationItemAdded());
        localStorage.removeItem(applicationFormProgressKey);

        return true;
    };

    const getAll = getStoredOnlineApplicationChoices;

    const clear = (): void => {
        ExecutionContext.ThrowIfServerSide();
        localStorage.removeItem(OnlineApplicationStateStorageKey);
        MessageBroker.publish(new OnlineApplicationItemRemoved());
        localStorage.removeItem(applicationFormProgressKey);
    };

    const remove = (subjectCode: string): void => {
        ExecutionContext.ThrowIfServerSide();
        let currentChoices = getAll();

        currentChoices = currentChoices.filter(choice => choice.subject.code !== subjectCode);

        localStorage.setItem(OnlineApplicationStateStorageKey, JSON.stringify(currentChoices));
        MessageBroker.publish(new OnlineApplicationItemRemoved());
        localStorage.removeItem(applicationFormProgressKey);
    };

    const setAll = (newChoices: OnlineApplicationChoice[]): boolean => {
        ExecutionContext.ThrowIfServerSide();

        const currentChoices = getAll();

        if (newChoices.length >= currentChoices.length && isCampusApplicationInProgress()) {
            MessageBroker.publish(new AddingDifferentApplicationTypeAttempt());

            return false;
        }

        localStorage.setItem(OnlineApplicationStateStorageKey, JSON.stringify(newChoices));
        MessageBroker.publish(new OnlineApplicationItemAdded());

        return true;
    };

    return { append, getAll, clear, remove, setAll };
};
